import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import PostLoginLoadingScreen from './PostLoginLoadingScreen'

function isClaimsBlockedPolicy(policy: PolicyLink | null | undefined): boolean {
  return policy?.claimsBlocked === true
}

/**
 * Blocks mid-flow claim routes when the policy cannot submit claims yet (future term start).
 * Redirects to Claims landing so the helper text replaces Make a claim.
 */
export default function RequireClaimsNotBlocked() {
  const location = useLocation()
  const { getPolicies } = useApi()
  const [state, setState] = useState<'checking' | 'allowed' | 'blocked'>('checking')
  const [blockedPolicyRef, setBlockedPolicyRef] = useState<string | null>(null)
  const [blockedPolicy, setBlockedPolicy] = useState<PolicyLink | null>(null)

  useEffect(() => {
    let cancelled = false
    const policyRef = decodePolicyRef(new URLSearchParams(location.search).get('policyRef'))

    if (!policyRef) {
      setState('allowed')
      return
    }

    const fromState = (location.state as { policy?: PolicyLink } | null)?.policy
    const fromCache = readPolicyFromListCache(policyRef)
    const quickHit =
      (fromState?.policyRef === policyRef && fromState) ||
      (fromCache?.policyRef === policyRef ? fromCache : null)

    if (isClaimsBlockedPolicy(quickHit)) {
      setBlockedPolicyRef(policyRef)
      setBlockedPolicy(quickHit)
      setState('blocked')
      return
    }

    const run = async () => {
      try {
        const response = await getPolicies()
        if (cancelled) return
        const match = response.policies?.find((p) => p.policyRef === policyRef) ?? null
        if (isClaimsBlockedPolicy(match)) {
          setBlockedPolicyRef(policyRef)
          setBlockedPolicy(match)
          setState('blocked')
          return
        }
        setState('allowed')
      } catch (error) {
        console.error('Error checking claims blocked status:', error)
        if (!cancelled) setState('allowed')
      }
    }

    void run()
    return () => {
      cancelled = true
    }
  }, [getPolicies, location.search, (location.state as { policy?: PolicyLink } | null)?.policy?.policyRef])

  if (state === 'checking') {
    return <PostLoginLoadingScreen />
  }

  if (state === 'blocked' && blockedPolicyRef) {
    return (
      <Navigate
        to={`/claims?policyRef=${encodeURIComponent(blockedPolicyRef)}`}
        replace
        state={blockedPolicy ? { policy: blockedPolicy } : undefined}
      />
    )
  }

  return <Outlet />
}
